import type { BoardObject, LineAnchor, AnchorSide } from '../core/interfaces/board-object.interface.js';
import type { ObjectRegistry } from '../core/object-registry.js';

/**
 * AnchorTarget is any BoardObject variant that carries a geometric bounding box
 * (x, y, width, height). Runtime identification is performed via the
 * `SlicePlugin.isAnchorTarget?()` plugin method (registry lookup) — the type
 * predicate only narrows to the geometric minimal interface. This keeps the
 * design Open/Closed: a future slice that declares `isAnchorTarget: () => true`
 * and exposes a bbox automatically becomes a valid anchor target without any
 * union edit. See PRD-F5 §TR8.
 */
export type AnchorTarget = BoardObject & {
  readonly width: number;
  readonly height: number;
};

/**
 * A world-space box a line can dock to. The box (x/y/width/height) is the
 * object's UNROTATED rectangle; `rotation` (degrees) + `pivotX`/`pivotY` describe
 * how it is turned on the board. Konva rotates a node around its origin
 * (`obj.x`/`obj.y`), so that origin is the pivot. Edge points computed on the
 * axis-aligned box are rotated around the pivot to land on the rotated edges.
 */
export interface AnchorBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly rotation?: number;
  readonly pivotX?: number;
  readonly pivotY?: number;
}

/** Rotate a point (px,py) around (cx,cy) by `deg` degrees (clockwise-positive). */
export function rotatePointAround(px: number, py: number, cx: number, cy: number, deg: number): { x: number; y: number } {
  if (!deg) return { x: px, y: py };
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const dx = px - cx;
  const dy = py - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/**
 * The box a line actually docks to for a given object. A slice may override its
 * anchorable box via `SlicePlugin.anchorBounds` — e.g. the person hugs its
 * visible avatar+name instead of its padded object box, so connector handles and
 * docked endpoints sit ON the person rather than floating at the box edge. Any
 * object without an override docks to its own x/y/width/height. The object's
 * rotation + pivot (its origin) are carried so docking follows a rotated object.
 */
export function anchorBoundsOf(obj: AnchorTarget, registry: ObjectRegistry): AnchorBox {
  const custom = registry.get(obj.type)?.anchorBounds?.(obj);
  const box = custom ?? { x: obj.x, y: obj.y, width: obj.width, height: obj.height };
  return { ...box, rotation: obj.rotation, pivotX: obj.x, pivotY: obj.y };
}

/**
 * Registry-driven type guard. The registry lookup is the Single Source of
 * Truth — a slice opts in by declaring `isAnchorTarget: () => true` in its
 * `SlicePlugin`. No hardcoded type-discriminator union remains here. See
 * PRD-F5 §TR8 (FC4).
 */
export function isAnchorTarget(
  obj: BoardObject | undefined | null,
  registry: ObjectRegistry,
): obj is AnchorTarget {
  if (obj == null) return false;
  return registry.get(obj.type)?.isAnchorTarget?.() === true;
}

/**
 * Convert an anchor (objectId + side + fraction) back into a world-space
 * point on the target object's edge. The caller is responsible for looking
 * up the target — we keep this helper pure so it is easy to test.
 */
export function resolveAnchor(anchor: LineAnchor, box: AnchorBox): { x: number; y: number } {
  const t = Math.max(0, Math.min(1, anchor.t));
  let p: { x: number; y: number };
  switch (anchor.side) {
    case 'top':    p = { x: box.x + t * box.width, y: box.y }; break;
    case 'bottom': p = { x: box.x + t * box.width, y: box.y + box.height }; break;
    case 'left':   p = { x: box.x,                 y: box.y + t * box.height }; break;
    case 'right':  p = { x: box.x + box.width,     y: box.y + t * box.height }; break;
  }
  // Turn the axis-aligned edge point onto the rotated edge (no-op when straight).
  return rotatePointAround(p.x, p.y, box.pivotX ?? box.x, box.pivotY ?? box.y, box.rotation ?? 0);
}

/**
 * Given a world-space point and a candidate target, return the nearest
 * snap candidate on that target's edges, or null if the point is farther
 * than `maxWorldDist` from every edge.
 */
export function nearestEdgePointOn(
  box: AnchorBox,
  objectId: string,
  worldX: number,
  worldY: number,
  maxWorldDist: number,
): { anchor: LineAnchor; x: number; y: number; dist: number } | null {
  const sides: Array<{ side: AnchorSide; x: number; y: number; t: number }> = [];

  // Work in the box's UNROTATED frame: turn the pointer back by -rotation around
  // the pivot, do the axis-aligned nearest-edge maths, then rotate the chosen
  // point forward again. Distances are rotation-invariant, so comparing in the
  // unrotated frame is exact.
  const rot = box.rotation ?? 0;
  const pivotX = box.pivotX ?? box.x;
  const pivotY = box.pivotY ?? box.y;
  const local = rotatePointAround(worldX, worldY, pivotX, pivotY, -rot);
  const px = local.x;
  const py = local.y;

  const left   = box.x;
  const right  = box.x + box.width;
  const top    = box.y;
  const bottom = box.y + box.height;

  const clampX = Math.min(right, Math.max(left, px));
  const clampY = Math.min(bottom, Math.max(top,  py));

  const widthSafe  = box.width  > 0 ? box.width  : 1;
  const heightSafe = box.height > 0 ? box.height : 1;

  sides.push({ side: 'top',    x: clampX, y: top,    t: (clampX - left) / widthSafe });
  sides.push({ side: 'bottom', x: clampX, y: bottom, t: (clampX - left) / widthSafe });
  sides.push({ side: 'left',   x: left,   y: clampY, t: (clampY - top)  / heightSafe });
  sides.push({ side: 'right',  x: right,  y: clampY, t: (clampY - top)  / heightSafe });

  let best: { anchor: LineAnchor; x: number; y: number; dist: number } | null = null;
  for (const s of sides) {
    const dx = s.x - px;
    const dy = s.y - py;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > maxWorldDist) continue;
    if (best && dist >= best.dist) continue;
    const world = rotatePointAround(s.x, s.y, pivotX, pivotY, rot);
    best = {
      anchor: { objectId, side: s.side, t: Math.max(0, Math.min(1, s.t)) },
      x: world.x,
      y: world.y,
      dist,
    };
  }
  return best;
}

/**
 * Find the best snap candidate across all valid targets.
 * `excludeId` lets the caller skip a target (e.g., the line itself, or the
 * object currently being dragged so it doesn't snap to itself).
 */
export function findSnap(
  objects: Iterable<BoardObject>,
  registry: ObjectRegistry,
  worldX: number,
  worldY: number,
  maxWorldDist: number,
  excludeId?: string,
): { anchor: LineAnchor; x: number; y: number } | null {
  let best: { anchor: LineAnchor; x: number; y: number; dist: number } | null = null;
  for (const obj of objects) {
    if (obj.id === excludeId) continue;
    if (!isAnchorTarget(obj, registry)) continue;
    const hit = nearestEdgePointOn(anchorBoundsOf(obj, registry), obj.id, worldX, worldY, maxWorldDist);
    if (!hit) continue;
    if (!best || hit.dist < best.dist) best = hit;
  }
  if (!best) return null;
  return { anchor: best.anchor, x: best.x, y: best.y };
}
